import React, { useEffect, useState } from 'react';
import { getOutlets } from '../../api/campaign';
import atlasLogo from '../../img/atlas.png';
// CAMPAIGN 2 IS ATLAS ONLY -- restore with the Innovate button below.
// import innovateLogo from '../../img/innovate.png';

export default function OutletPage() {
    const [outlets, setOutlets] = useState([]);
    const [step, setStep] = useState(1);
    const [shopName, setShopName] = useState("");
    const [shopId, setShopId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [brand, setBrand] = useState(0);
    const [atlasItems, setAtlasItems] = useState([]);
    const [innovateItems, setInnovateItems] = useState([]);
    const [billItems, setBillItems] = useState([{ id: Date.now(), item: '', qty: '' }]);
    
    const atlas_items = ['Atlas A', 'Atlas B', 'Atlas C', 'Atlas D', 'Atlas E', 'Atlas F', 'Atlas G', 'Atlas H', 'Atlas I', 'Atlas J'];
    const innovate_items = ['B5', 'EXERCISE', 'CR'];
    
    // Color palettes for brands
    const colors = {
        innovate: {
            bgFrom: '#1A0D2E',
            bgVia: '#5D1A5E',
            bgTo: '#B81F7B',
            primary: '#EC4899',
            primaryLight: '#F472B6',
            buttonFrom: '#EC4899',
            buttonTo: '#F472B6',
            textLight: '#FBCFE8',
            glow: 'rgba(236, 72, 153, 0.3)'
        },
        atlas: {
            bgFrom: '#8B2500',
            bgVia: '#C4621F',
            bgTo: '#E87722',
            primary: '#F59E42',
            primaryLight: '#FFA94D',
            buttonFrom: '#F59E42',
            buttonTo: '#FFA94D',
            textLight: '#FFEDD5',
            glow: 'rgba(245, 158, 66, 0.3)'
        }
    };

    // CAMPAIGN 2 IS ATLAS ONLY -- always Atlas colours.
    // Original: brand === 2 ? colors.atlas : colors.innovate
    const currentColors = colors.atlas;
    
    useEffect(() => {
        fetchOutlet();
        setAtlasItems(atlas_items);
        setInnovateItems(innovate_items);
    }, []);

    const fetchOutlet = async () => {
        try {
            setOutlets(await getOutlets());
        } catch (error) {
            console.log("error fetch outlet name: ", error);
        }
    };

    // Filter outlets based on search query
    const filteredOutlets = outlets.filter(outlet =>
        outlet.shopName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleShopSelect = (outlet) => {
        setShopName(outlet.shopName);
        setSearchQuery(outlet.shopName);
        setShopId(outlet.shopId);
        setShowSuggestions(false);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setShowSuggestions(true);
        setShopName(""); // Clear selected shop if user types
    };

    // Selecting a shop now goes straight into the game. Campaign 2 is Atlas
    // only, so the brand step (step 2) has nothing left to choose.
    // Original: setStep(2);
    const handleNextToStep2 = () => {
        if (!shopName) {
            alert("Please select a valid shop from the list");
            return;
        }
        window.location.href = `/?b=2&t=${shopId}`;
    };

    const handleBrandSelect = (brandId) => {
        setBrand(brandId);
        setStep(3);
    };

    const handleItemChange = (id, field, value) => {
        const newBillItems = billItems.map(item => 
            item.id === id ? { ...item, [field]: value } : item
        );
        
        // Find the item that was just updated
        const updatedItem = newBillItems.find(item => item.id === id);
        
        // Check if this is the last item and both fields are filled
        const isLastItem = billItems[billItems.length - 1].id === id;
        if (isLastItem && updatedItem.item && updatedItem.qty) {
            setBillItems([...newBillItems, { id: Date.now(), item: '', qty: '' }]);
        } else {
            setBillItems(newBillItems);
        }
    };

    const handleRemoveItem = (id) => {
        if (billItems.length > 1) {
            const newBillItems = billItems.filter(item => item.id !== id);
            setBillItems(newBillItems);
        }
    };

    const handleFinishBill = () => {
        // Filter out empty rows and remove id field
        const validItems = billItems
            .filter(item => item.item && item.qty)
            .map(({ id, ...itemData }) => itemData); // Remove id from submitted data
        
        if (validItems.length === 0) {
            alert("Please add at least one item");
            return;
        }

        console.log("Bill Data:", {
            shop: shopName,
            brand: brand === 1 ? "Innovate" : "Atlas",
            items: validItems
        });

        // Here you can send data to backend
        alert("Bill finished successfully!");
        
        // Reset for next bill
        setStep(1);
        setShopName("");
        setSearchQuery("");
        setBrand(0);
        setBillItems([{ id: Date.now(), item: '', qty: '' }]);
    };

    const currentItems = brand === 1 ? innovateItems : atlasItems;

    return (
        <div 
            className='min-h-screen flex items-center justify-center p-6 relative overflow-hidden'
            style={{
                // Atlas background on every step (was Innovate purple unless step 3)
                background: `linear-gradient(to bottom right, ${currentColors.bgFrom}, ${currentColors.bgVia}, ${currentColors.bgTo})`
            }}
        >
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-40 h-40 rounded-full blur-3xl animate-float" style={{ backgroundColor: currentColors.glow }}></div>
                <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full blur-3xl animate-float-delayed" style={{ backgroundColor: currentColors.glow }}></div>
                <div className="absolute top-1/2 left-1/3 w-36 h-36 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: currentColors.glow }}></div>
            </div>

            <div className="relative z-10 w-full max-w-3xl">
                {/* Progress Indicator -- hidden: shop selection is the only step
                    now that brand choice is gone, so a 1-2-3 tracker just
                    implies steps that never come. */}
                <div className="mb-8 hidden items-center justify-center gap-4">
                    {[1, 2, 3].map((num) => (
                        <div key={num} className="flex items-center">
                            <div 
                                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                                    step >= num 
                                        ? 'text-white shadow-lg' 
                                        : 'bg-white/20 text-white/50'
                                }`}
                                style={{ 
                                    background: step >= num ? `linear-gradient(to right, ${currentColors.primary}, ${currentColors.primaryLight})` : undefined
                                }}
                            >
                                {num}
                            </div>
                            {num < 3 && (
                                <div 
                                    className={`w-16 h-1 mx-2 transition-all duration-300 ${
                                        step > num ? 'opacity-100' : 'bg-white/20 opacity-50'
                                    }`}
                                    style={{ 
                                        background: step > num ? `linear-gradient(to right, ${currentColors.primary}, ${currentColors.primaryLight})` : undefined
                                    }}
                                ></div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Stage 1: Shop Selection */}
                {step === 1 && (
                    <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20 animate-slide-up">
                        <div className="text-center mb-8">
                            <h1 className='text-4xl font-bold text-white drop-shadow-2xl mb-2'>Select Outlet</h1>
                            <p className="text-white/80 text-sm">Choose your shop from the list</p>
                        </div>

                        <div className="relative mb-6">
                            <input 
                                type='text'
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onFocus={() => setShowSuggestions(true)}
                                placeholder="Type to search shops..."
                                className='w-full bg-white/90 border-2 border-white/50 rounded-2xl px-5 py-4 shadow-lg placeholder:text-gray-500 focus:ring-4 focus:ring-pink-400/50 focus:border-pink-400 transition-all duration-300 font-semibold text-lg'
                                style={{ color: '#1F2937' }}
                            />
                            
                            {/* Suggestions Dropdown */}
                            {showSuggestions && searchQuery && (
                                <div className="absolute w-full mt-2 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 max-h-60 overflow-y-auto z-20 animate-fade-in">
                                    {filteredOutlets.length > 0 ? (
                                        filteredOutlets.map((outlet, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleShopSelect(outlet)}
                                                className="px-5 py-3 hover:bg-pink-100 cursor-pointer transition-colors duration-200 text-gray-800 font-medium border-b border-gray-100 last:border-b-0"
                                            >
                                                {outlet.shopName}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-5 py-3 text-gray-500 text-center">
                                            No shops found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {shopName && (
                            <div className="bg-green-500/20 border border-green-400/30 rounded-2xl p-4 mb-6 animate-scale-in">
                                <p className="text-white text-center font-semibold">
                                    ✓ Selected: <span className="text-green-300">{shopName}</span>
                                </p>
                            </div>
                        )}

                        <button
                            onClick={handleNextToStep2}
                            className="w-full text-white font-bold text-xl rounded-2xl px-8 py-5 shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 relative overflow-hidden group"
                            style={{ 
                                background: `linear-gradient(to right, ${colors.innovate.buttonFrom}, ${colors.innovate.buttonTo})`,
                                boxShadow: `0 20px 50px ${colors.innovate.glow}`
                            }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                            <span className="relative z-10">Next Step →</span>
                        </button>
                    </div>
                )}

                {/* Stage 2: Brand Selection */}
                {step === 2 && (
                    <div className="animate-slide-up">
                        <div className="text-center mb-8">
                            <h1 className='text-4xl font-bold text-white drop-shadow-2xl mb-2'>Select</h1>
                            <p className="text-white/80 text-sm">Select Atlas</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* CAMPAIGN 2 IS ATLAS ONLY -- Innovate button switched off.
                                Restore this block (and the innovateLogo import) to bring it back.
                            <button
                                onClick={() => window.location.href = `/?b=1&t=${shopId}`}
                                className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border-2 border-white/30 shadow-2xl transition-all duration-300 transform hover:scale-105 hover:border-pink-400/50 active:scale-95 group"
                            >
                                <div className="text-center">
                                    <div className="mb-4 flex justify-center">
                                        <img
                                            src={innovateLogo}
                                            alt="Innovate"
                                            className="h-20 w-auto"
                                        />
                                    </div>
                                    <h2 className="text-3xl font-bold text-white mb-2">Innovate</h2>
                                    <div
                                        className="h-2 rounded-full mx-auto w-32 group-hover:w-full transition-all duration-300"
                                        style={{ background: `linear-gradient(to right, ${colors.innovate.primary}, ${colors.innovate.primaryLight})` }}
                                    ></div>
                                </div>
                            </button>
                            */}

                            {/* Atlas Button */}
                            <button
                                onClick={() => window.location.href = `/?b=2&t=${shopId}`}
                                className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border-2 border-white/30 shadow-2xl transition-all duration-300 transform hover:scale-105 hover:border-orange-400/50 active:scale-95 group"
                            >
                                <div className="text-center">
                                    <div className="mb-4 flex justify-center">
                                        <img 
                                            src={atlasLogo} 
                                            alt="Atlas" 
                                            className="h-20 w-auto"
                                        />
                                    </div>
                                    <h2 className="text-3xl font-bold text-white mb-2">Atlas</h2>
                                    <div 
                                        className="h-2 rounded-full mx-auto w-32 group-hover:w-full transition-all duration-300"
                                        style={{ background: `linear-gradient(to right, ${colors.atlas.primary}, ${colors.atlas.primaryLight})` }}
                                    ></div>
                                </div>
                            </button>

                            {/* Billing Button */}
                            {/* <button
                                onClick={() => handleBrandSelect(1)}
                                className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border-2 border-white/30 shadow-2xl transition-all duration-300 transform hover:scale-105 hover:border-orange-400/50 active:scale-95 group"
                            >
                                <div className="text-center"> */}
                                    {/* <div className="mb-4 flex justify-center">
                                        <img 
                                            src={atlasLogo} 
                                            alt="Atlas" 
                                            className="h-20 w-auto"
                                        />
                                    </div> */}
                                    {/* <h2 className="text-3xl font-bold text-white mb-2">Billing</h2>
                                    <div 
                                        className="h-2 rounded-full mx-auto w-32 group-hover:w-full transition-all duration-300"
                                        style={{ background: `linear-gradient(to right, ${colors.atlas.primary}, ${colors.atlas.primaryLight})` }}
                                    ></div>
                                </div>
                            </button> */}
                        </div>

                        <button
                            onClick={() => setStep(1)}
                            className="mt-6 w-full bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold text-lg rounded-2xl px-6 py-3 hover:bg-white/20 transition-all duration-300"
                        >
                            ← Back
                        </button>
                    </div>
                )}

                {/* Stage 3: Item & Quantity Billing */}
                {step === 3 && (
                    <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20 animate-slide-up">
                        <div className="text-center mb-8">
                            <h1 className='text-4xl font-bold text-white drop-shadow-2xl mb-2'>Add Items</h1>
                            <p className="text-white/80 text-sm">Shop: <span className="font-semibold">{shopName}</span> | Brand: <span className="font-semibold">{brand === 1 ? 'Innovate' : 'Atlas'}</span></p>
                        </div>

                        <div className="space-y-4 mb-6 max-h-96 overflow-y-auto pr-2">
                            {billItems.map((item, index) => (
                                <div 
                                    key={item.id}
                                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 animate-scale-in"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <div className="flex gap-4 items-center">
                                        <div className="flex-1">
                                            <label className="block text-white text-sm font-semibold mb-2">Item</label>
                                            <select
                                                value={item.item}
                                                onChange={(e) => handleItemChange(item.id, 'item', e.target.value)}
                                                className="w-full bg-white/90 border-2 border-white/50 rounded-xl px-4 py-3 focus:ring-4 focus:border-white transition-all duration-300 font-semibold"
                                                style={{ 
                                                    color: '#1F2937',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    appearance: 'none',
                                                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 0.75rem center',
                                                    backgroundSize: '1.5em 1.5em',
                                                    paddingRight: '2.5rem'
                                                }}
                                            >
                                                <option value="" style={{ color: '#6B7280' }}>Select Item</option>
                                                {currentItems.map((itemName, idx) => (
                                                    <option key={idx} value={itemName} style={{ color: '#1F2937' }}>{itemName}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="w-32">
                                            <label className="block text-white text-sm font-semibold mb-2">Qty</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.qty}
                                                onChange={(e) => handleItemChange(item.id, 'qty', e.target.value)}
                                                placeholder="0"
                                                className="w-full bg-white/90 border-2 border-white/50 rounded-xl px-4 py-3 focus:ring-4 focus:border-white transition-all duration-300 text-center font-bold text-lg"
                                                style={{ color: '#1F2937' }}
                                            />
                                        </div>

                                        {billItems.length > 1 && (
                                            <button
                                                onClick={() => handleRemoveItem(item.id)}
                                                className="mt-6 bg-red-500/80 hover:bg-red-600 text-white rounded-xl px-4 py-3 transition-all duration-300 transform hover:scale-105"
                                            >
                                                🗑️
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={() => setStep(2)}
                                className="flex-1 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold text-lg rounded-2xl px-6 py-4 hover:bg-white/20 transition-all duration-300"
                            >
                                ← Back
                            </button>

                            <button
                                onClick={handleFinishBill}
                                className="flex-1 text-white font-bold text-xl rounded-2xl px-8 py-4 shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 relative overflow-hidden group"
                                style={{ 
                                    background: `linear-gradient(to right, ${currentColors.buttonFrom}, ${currentColors.buttonTo})`,
                                    boxShadow: `0 20px 50px ${currentColors.glow}`
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                <span className="relative z-10">✓ Finish Bill</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                @keyframes float { 0%, 100% { transform: translateY(0px) translateX(0px); } 50% { transform: translateY(-20px) translateX(10px); } }
                @keyframes float-delayed { 0%, 100% { transform: translateY(0px) translateX(0px); } 50% { transform: translateY(20px) translateX(-10px); } }
                @keyframes slide-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
                @keyframes bounce-subtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
                .animate-float { animation: float 6s ease-in-out infinite; }
                .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
                .animate-slide-up { animation: slide-up 0.6s ease-out forwards; }
                .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
                .animate-scale-in { animation: scale-in 0.4s ease-out forwards; }
                .animate-bounce-subtle { animation: bounce-subtle 2s ease-in-out infinite; }
            `}</style>
        </div>
    );
}